if (!customElements.get('product-reviews')) {
  function reviewText(value) {
    return String(value ?? '')
      .replace(/\r\n/g, '\n')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n{2,}/g, '\n')
      .trim();
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function starStep(count) {
    const steps = [0, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5];
    const value = Math.max(0, Math.min(5, Number(count) || 0));
    return steps.reduce((best, step) => {
      const bestDistance = Math.abs(best - value);
      const stepDistance = Math.abs(step - value);
      return stepDistance <= bestDistance ? step : best;
    }, 0);
  }

  function starsMarkup(count, assets, summary) {
    const step = starStep(count);
    const src = assets[String(step)] || assets['0'];
    const className = summary ? 'product-reviews__stars product-reviews__stars--summary' : 'product-reviews__stars';
    return `<img class="${className}" src="${escapeHtml(src)}" alt="${step} out of 5 stars" width="512" height="96">`;
  }

  function formatDate(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-GB', { month: 'long' });
    return `${day} ${month}, ${date.getFullYear()}`;
  }

  function reviewName(review) {
    return review?.consumer?.displayName || review?.consumer?.name || 'Customer';
  }

  function mergeSummary(nativeSummary, importedSummary) {
    const native = nativeSummary?.numberOfReviews || {};
    const imported = importedSummary?.numberOfReviews || {};
    const counts = {
      1: (native.oneStar || 0) + (imported.oneStar || 0),
      2: (native.twoStars || 0) + (imported.twoStars || 0),
      3: (native.threeStars || 0) + (imported.threeStars || 0),
      4: (native.fourStars || 0) + (imported.fourStars || 0),
      5: (native.fiveStars || 0) + (imported.fiveStars || 0),
    };
    const nativeTotal = native.total || 0;
    const importedTotal = imported.total || 0;
    const total = nativeTotal + importedTotal;
    const average = total
      ? ((nativeSummary?.starsAverage || 0) * nativeTotal + (importedSummary?.starsAverage || 0) * importedTotal) / total
      : 0;
    return { counts, total, average };
  }

  async function fetchJson(url, apiKey, signal) {
    const response = await fetch(url, {
      method: 'GET',
      headers: { apikey: apiKey },
      signal,
    });
    if (!response.ok) throw new Error(`Trustpilot ${response.status}`);
    return response.json();
  }

  class ProductReviews extends HTMLElement {
    constructor() {
      super();
      this.onFilterChange = this.onFilterChange.bind(this);
      this.onMoreClick = this.onMoreClick.bind(this);
    }

    connectedCallback() {
      this.session = (this.session || 0) + 1;
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.apiKey = this.dataset.apiKey;
      this.businessUnitId = this.dataset.businessUnitId;
      this.skus = (this.dataset.skus || '')
        .split(',')
        .map((sku) => sku.trim())
        .filter(Boolean);
      this.perPage = Math.max(1, Number(this.dataset.perPage) || 5);
      this.language = (this.dataset.language || '').trim();
      this.selectedStars = new Set(
        (this.dataset.defaultStars || '')
          .split(',')
          .map((star) => Number(star))
          .filter((star) => star >= 1 && star <= 5)
      );
      if (!this.selectedStars.size) this.selectedStars.add(5);
      this.scoreEl = this.querySelector('[data-product-reviews-score]');
      this.filtersEl = this.querySelector('[data-product-reviews-filters]');
      this.listEl = this.querySelector('[data-product-reviews-list]');
      this.moreEl = this.querySelector('[data-product-reviews-more]');
      this.base = `https://api.trustpilot.com/v1/product-reviews/business-units/${this.businessUnitId}`;
      this.skuQuery = this.skus.map(encodeURIComponent).join(',');
      this.starAssets = JSON.parse(this.querySelector('[data-star-assets]')?.textContent || '{}');
      this.readMoreButton = this.querySelector('[data-read-more-button]')?.innerHTML.trim() || '';
      this.state = {
        reviews: [],
        nativeBuffer: [],
        importedBuffer: [],
        nativePage: 0,
        importedPage: 0,
        nativeHasMore: true,
        importedHasMore: true,
        requestId: 0,
      };
      this.filtersEl?.removeEventListener('change', this.onFilterChange);
      this.moreEl?.removeEventListener('click', this.onMoreClick);
      this.filtersEl?.addEventListener('change', this.onFilterChange);
      this.moreEl?.addEventListener('click', this.onMoreClick);
      this.listenersBound = true;
      const session = this.session;
      this.loadSummary()
        .then((summary) => {
          if (!this.isCurrent(session)) return;
          if (!summary.total && this.dataset.designMode !== 'true') {
            const section = this.closest('.product-reviews');
            if (section) section.hidden = true;
            return;
          }
          if (!summary.total) {
            this.setStatus(this.dataset.empty);
            return;
          }
          this.renderScore(summary);
          this.renderFilters(summary);
          return this.loadPage(false);
        })
        .catch((error) => {
          if (error?.name === 'AbortError' || !this.isCurrent(session)) return;
          this.setStatus(this.dataset.error);
        });
    }

    disconnectedCallback() {
      this.session = (this.session || 0) + 1;
      this.abortController?.abort();
      if (!this.listenersBound) return;
      this.filtersEl?.removeEventListener('change', this.onFilterChange);
      this.moreEl?.removeEventListener('click', this.onMoreClick);
      this.listenersBound = false;
    }

    isCurrent(session) {
      return session === this.session && !this.abortController?.signal.aborted;
    }

    onFilterChange(event) {
      const input = event.target.closest('input[type="checkbox"]');
      if (!input) return;
      const star = Number(input.value);
      if (input.checked) {
        this.selectedStars.add(star);
      } else if (this.selectedStars.size === 1) {
        input.checked = true;
        return;
      } else {
        this.selectedStars.delete(star);
      }
      this.loadPage(false);
    }

    onMoreClick() {
      this.loadPage(true);
    }

    query(params) {
      return Object.entries(params)
        .filter(([, value]) => value !== '' && value != null)
        .map(([key, value]) => `${encodeURIComponent(key)}=${value}`)
        .join('&');
    }

    listUrl(path, page) {
      const stars = [...this.selectedStars].sort().join(',');
      return `${this.base}${path}?${this.query({
        sku: this.skuQuery,
        page,
        perPage: this.perPage,
        language: this.language ? encodeURIComponent(this.language) : '',
        stars: this.selectedStars.size && this.selectedStars.size < 5 ? stars : '',
      })}`;
    }

    setStatus(message) {
      this.scoreEl.innerHTML = `<p class="product-reviews__status">${escapeHtml(message)}</p>`;
    }

    renderScore(summary) {
      const average = summary.total ? (Math.round(summary.average * 10) / 10).toFixed(1) : '0.0';
      const countLabel = `${summary.total} ${summary.total === 1 ? 'review' : 'reviews'}`;
      this.scoreEl.innerHTML = `
        <p class="product-reviews__score">
          ${starsMarkup(summary.average, this.starAssets, true)}
          <span class="product-reviews__score-value">${average}</span>
          <span class="product-reviews__score-max">/5</span>
          <span class="product-reviews__score-count">· ${countLabel}</span>
          <span class="visually-hidden">${average} out of 5 from ${countLabel}</span>
        </p>
      `;
    }

    renderFilters(summary) {
      const max = Math.max(...Object.values(summary.counts), 0);
      this.filtersEl.innerHTML = `<div class="product-reviews__filters">${[5, 4, 3, 2, 1]
        .map((star) => {
          const count = summary.counts[star] || 0;
          const width = max ? Math.round((count / max) * 100) : 0;
          return `
            <label class="product-reviews__filter">
              <input type="checkbox" value="${star}" ${this.selectedStars.has(star) ? 'checked' : ''}>
              <span class="product-reviews__filter-label">${star}-stars</span>
              <span class="product-reviews__bar" aria-hidden="true"><span style="width:${width}%"></span></span>
              <span class="product-reviews__count">${count}</span>
            </label>
          `;
        })
        .join('')}</div>`;
    }

    verifiedMarkup() {
      const label = this.dataset.verifiedLabel || 'Verified, collected by';
      const name = this.dataset.collectorName || '';
      const icon = this.dataset.verifiedIcon;
      const profile = this.dataset.profileUrl;
      const text = `${escapeHtml(label)}${name ? `<br>${escapeHtml(name)}` : ''}`;
      const content = profile
        ? `<a href="${escapeHtml(profile)}" target="_blank" rel="noopener">${text}</a>`
        : text;
      return `<p class="product-reviews__verified"><img src="${escapeHtml(icon)}" alt="" width="9" height="9">${content}</p>`;
    }

    clampedHtml(html, limit) {
      if (!html || !limit || limit === 'none') return html;
      return `<read-more class="read-more__text read-more__text--inline" data-line-clamp="${escapeHtml(limit)}" data-toggle-class="text-limit-${escapeHtml(limit)}" data-show-text="${escapeHtml(this.dataset.readMore || 'Read more')}" data-hide-text="${escapeHtml(this.dataset.readLess || 'Read less')}"><div class="read-more__content rte"><div class="read-more__body">${html}</div>${this.readMoreButton || ''}</div></read-more>`;
    }

    replyMarkup(comment) {
      if (!comment?.comment) return '';
      return `
        <div class="product-reviews__reply">
          <img class="product-reviews__reply-hook" src="${escapeHtml(this.dataset.hook)}" alt="" width="20" height="38">
          <div class="product-reviews__reply-head">
            <img class="product-reviews__reply-mark" src="${escapeHtml(this.dataset.mark)}" alt="" width="42" height="42">
            <div class="product-reviews__reply-meta">
              <p class="product-reviews__reply-author">${escapeHtml(this.dataset.replyLabel || 'Reply')}</p>
              <p class="product-reviews__reply-date">${escapeHtml(formatDate(comment.createdAt))}</p>
            </div>
          </div>
          <div class="product-reviews__reply-body">${this.clampedHtml(`<p class="product-reviews__reply-copy">${escapeHtml(reviewText(comment.comment))}</p>`, this.dataset.replyLineLimit)}</div>
        </div>
      `;
    }

    renderList() {
      if (!this.state.reviews.length) {
        this.listEl.innerHTML = `<p class="product-reviews__status">${escapeHtml(
          this.selectedStars.size ? this.dataset.noMatch : this.dataset.empty
        )}</p>`;
        return;
      }

      this.listEl.innerHTML = this.state.reviews
        .map((review) => {
          const content = reviewText(review.content);
          return `
            <article class="product-reviews__review">
              <div class="product-reviews__review-meta">
                ${starsMarkup(review.stars, this.starAssets)}
                <p class="product-reviews__name">${escapeHtml(reviewName(review))}</p>
                <p class="product-reviews__date">${escapeHtml(formatDate(review.createdAt))}</p>
                ${review.imported ? this.verifiedMarkup() : ''}
              </div>
              <div class="product-reviews__review-content">
                ${content ? this.clampedHtml(`<p class="product-reviews__body">${escapeHtml(content)}</p>`, this.dataset.reviewLineLimit) : ''}
                ${this.replyMarkup(review.firstCompanyComment)}
              </div>
            </article>
          `;
        })
        .join('');
    }

    updateMore() {
      const hasMore = this.state.nativeBuffer.length || this.state.importedBuffer.length || this.state.nativeHasMore || this.state.importedHasMore;
      this.moreEl.hidden = !hasMore || !this.state.reviews.length;
      this.moreEl.disabled = false;
    }

    reviewKey(review) {
      return review.id || `${review.imported}-${review.createdAt}-${reviewName(review)}-${review.content}`;
    }

    reviewTime(review) {
      return new Date(review?.createdAt || 0).getTime();
    }

    resetReviews() {
      this.state.reviews = [];
      this.state.nativeBuffer = [];
      this.state.importedBuffer = [];
      this.state.nativePage = 0;
      this.state.importedPage = 0;
      this.state.nativeHasMore = true;
      this.state.importedHasMore = true;
    }

    async fetchReviews(url) {
      return fetchJson(url, this.apiKey, this.abortController.signal);
    }

    async fillSource(source) {
      const isNative = source === 'native';
      if (isNative ? !this.state.nativeHasMore : !this.state.importedHasMore) return;
      const page = (isNative ? this.state.nativePage : this.state.importedPage) + 1;
      const data = await this.fetchReviews(this.listUrl(isNative ? '/reviews' : '/imported-reviews', page));
      const batch = data?.productReviews || [];
      const seen = new Set([...this.state.reviews, ...this.state.nativeBuffer, ...this.state.importedBuffer].map((review) => this.reviewKey(review)));
      const fresh = batch
        .map((review) => ({ ...review, imported: !isNative }))
        .filter((review) => {
          const key = this.reviewKey(review);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .sort((a, b) => this.reviewTime(b) - this.reviewTime(a));
      if (isNative) {
        this.state.nativePage = page;
        this.state.nativeHasMore = batch.length === this.perPage;
        this.state.nativeBuffer.push(...fresh);
      } else {
        this.state.importedPage = page;
        this.state.importedHasMore = batch.length === this.perPage;
        this.state.importedBuffer.push(...fresh);
      }
    }

    takeNext(count) {
      const taken = [];
      while (taken.length < count && (this.state.nativeBuffer.length || this.state.importedBuffer.length)) {
        const native = this.state.nativeBuffer[0];
        const imported = this.state.importedBuffer[0];
        const useNative = native && (!imported || this.reviewTime(native) >= this.reviewTime(imported));
        taken.push(useNative ? this.state.nativeBuffer.shift() : this.state.importedBuffer.shift());
      }
      return taken;
    }

    async loadPage(append) {
      const session = this.session;
      const requestId = ++this.state.requestId;
      if (!this.apiKey || !this.businessUnitId || !this.skus.length) {
        this.setStatus(this.dataset.error);
        return;
      }
      if (!this.selectedStars.size) {
        this.resetReviews();
        this.state.nativeHasMore = false;
        this.state.importedHasMore = false;
        this.renderList();
        this.updateMore();
        return;
      }
      if (!append) {
        this.resetReviews();
        this.listEl.innerHTML = `<p class="product-reviews__status">${escapeHtml(this.dataset.loading)}</p>`;
      }
      this.moreEl.disabled = true;
      const target = this.state.reviews.length + this.perPage;

      while (this.state.reviews.length < target) {
        const requests = [];
        if (!this.state.nativeBuffer.length && this.state.nativeHasMore) requests.push(this.fillSource('native'));
        if (!this.state.importedBuffer.length && this.state.importedHasMore) requests.push(this.fillSource('imported'));
        if (requests.length) {
          const results = await Promise.allSettled(requests);
          if (!this.isCurrent(session) || requestId !== this.state.requestId) return;
          const aborted = results.length && results.every((result) => result.status === 'rejected' && result.reason?.name === 'AbortError');
          if (aborted) return;
          const failed = results.length && results.every((result) => result.status === 'rejected');
          if (failed && !this.state.reviews.length && !this.state.nativeBuffer.length && !this.state.importedBuffer.length) {
            this.setStatus(this.dataset.error);
            this.listEl.innerHTML = '';
            this.moreEl.hidden = true;
            return;
          }
        }
        const before = this.state.reviews.length;
        this.state.reviews.push(...this.takeNext(target - this.state.reviews.length));
        if (this.state.reviews.length === before) break;
      }

      if (!this.isCurrent(session) || requestId !== this.state.requestId) return;
      this.renderList();
      this.updateMore();
    }

    async loadSummary() {
      const summaryQuery = this.query({ sku: this.skuQuery });
      const [nativeResult, importedResult] = await Promise.allSettled([
        this.fetchReviews(`${this.base}?${summaryQuery}`),
        this.fetchReviews(`${this.base}/imported-reviews-summaries?${summaryQuery}`),
      ]);
      if (nativeResult.status === 'rejected' && importedResult.status === 'rejected') {
        throw nativeResult.reason;
      }
      return mergeSummary(
        nativeResult.status === 'fulfilled' ? nativeResult.value : null,
        importedResult.status === 'fulfilled' ? importedResult.value : null
      );
    }
  }

  customElements.define('product-reviews', ProductReviews);
  }
